"use client";

import { MindstringPrototype, SupportedLanguage } from "@/generated/fastapi";
import {
  Button,
  Textarea,
  Field,
  Flex,
  Text,
  VStack,
  Badge,
  Collapsible,
  Float,
  Circle,
} from "@chakra-ui/react";
import {
  useSituationAccessors,
  useSituationsQuery,
} from "@/features/situations";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import { mindstringPrototypesSlang } from "@/shared/config/backend";
import { getPrototypeBadgeTokens } from "@/shared/config/prototypeTheme";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_LOADING_STATES,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping";

import useInsertThoughtsForm from "../hooks/useInsertThoughtsForm";

function formatSituationLabel({
  context,
  position,
  abbr,
}: {
  abbr?: string | null;
  context?: string | null;
  position?: string | null;
}) {
  const contextLabel = [position, context].filter(Boolean).join(", ");

  return abbr || contextLabel || "Chosen situation";
}

type MindstringDetailsItem = {
  count: number;
  index: number;
  isValid: boolean;
  text: string;
};

type ThoughtDetailsReportProps = {
  mindstrings: MindstringDetailsItem[];
};

function ThoughtDetailsReport({ mindstrings }: ThoughtDetailsReportProps) {
  if (mindstrings.length === 0) return null;

  const invalidMindstringCount = mindstrings.filter(
    (mindstring) => !mindstring.isValid,
  ).length;
  const allMindstringsValid = invalidMindstringCount === 0;
  const mindstringLabel =
    mindstrings.length === 1 ? "1 thought" : `${mindstrings.length} thoughts`;
  const statusLabel = allMindstringsValid
    ? "Ready"
    : `${invalidMindstringCount} need review`;
  const helperText = allMindstringsValid
    ? "Each thought is within the required length."
    : "Review the highlighted thoughts before inserting.";

  return (
    <Collapsible.Root defaultOpen unmountOnExit={false}>
      <Collapsible.Trigger paddingY="3" width="full">
        <Flex align="center" justify="space-between" gap="3" wrap="wrap">
          <Text fontSize="sm" fontWeight="medium">
            Message details
          </Text>
          <Flex align="center" gap="2" wrap="wrap">
            <Badge variant="subtle" colorPalette="gray">
              {mindstringLabel}
            </Badge>
            <Badge
              variant={allMindstringsValid ? "subtle" : "solid"}
              colorPalette={allMindstringsValid ? "green" : "red"}
            >
              {statusLabel}
            </Badge>
          </Flex>
        </Flex>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <VStack align="stretch" gap="2">
          <Text fontSize="xs" color="moveon.text.muted">
            {helperText}
          </Text>

          {mindstrings.map((mindstring) => (
            <Flex
              key={mindstring.index}
              p="2"
              borderRadius="md"
              bg={
                mindstring.isValid
                  ? "moveon.successLight"
                  : "moveon.dangerLight"
              }
              borderWidth="1"
              borderColor={
                mindstring.isValid
                  ? "moveon.successBorder"
                  : "moveon.dangerBorder"
              }
              justify="space-between"
              align="center"
              gap="3"
            >
              <Text fontSize="xs" minW="0" color="moveon.surface.fg">
                <Text as="span" fontWeight="medium">
                  #{mindstring.index}:
                </Text>{" "}
                {mindstring.text}
              </Text>
              <Badge
                colorPalette={mindstring.isValid ? "green" : "red"}
                variant="solid"
                fontSize="xs"
                flexShrink="0"
              >
                {mindstring.count} chars
              </Badge>
            </Flex>
          ))}
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

interface InsertThoughtsFormProps {
  situationId: string;
  prototype: MindstringPrototype;
  showOrientation?: boolean;
}

export default function InsertThoughtsForm({
  situationId,
  prototype,
  showOrientation = true,
}: InsertThoughtsFormProps) {
  const situationsQuery = useSituationsQuery();
  const { getSituationById } = useSituationAccessors(situationsQuery.data);
  const situation = getSituationById(situationId);
  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    watchedLanguage,
    selectedLanguages,
    detectedLanguage,
    autoDetectedLanguage,
    isDetectedLanguageSupported,
    mindstringsWithCounts,
    mindstringCount,
    onSubmit,
    handleLanguageSelection,
    languageItems,
    languageCodeToNameMap,
  } = useInsertThoughtsForm({
    situationId,
    prototype,
  });

  const prototypeLabel =
    mindstringPrototypesSlang[prototype]?.label ?? prototype;
  const prototypeBadgeTokens = getPrototypeBadgeTokens(prototype);
  const languageLabel = watchedLanguage
    ? (languageCodeToNameMap[watchedLanguage] ?? watchedLanguage)
    : "Choose language";
  const destinationLabel = situation
    ? formatSituationLabel(situation)
    : "Chosen situation";
  const isReadyToSubmit = mindstringCount > 0 && Boolean(watchedLanguage);
  const submitHelperText =
    mindstringCount === 0
      ? "Add one thought per line before inserting."
      : !watchedLanguage
        ? "Choose the language for these thoughts."
        : `${mindstringCount} ${
            mindstringCount === 1 ? "thought" : "thoughts"
          } ready to insert.`;
  const countIndicatorBg =
    errors.thoughtText != null
      ? "moveon.danger"
      : mindstringCount > 0
        ? "moveon.success"
        : "moveon.inactive";
  const countIndicatorColor =
    errors.thoughtText != null || mindstringCount > 0
      ? "white"
      : "moveon.text.muted";

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      handleSubmit(onSubmit)();
      event.preventDefault();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spaceY={4} align="stretch">
        {showOrientation ? (
          <GenericDrawerInfo
            description={PROFILE_SHAPING_MESSAGES.addManually}
            items={[
              {
                label: PROFILE_SHAPING_LABELS.manualPhase,
                colorPalette: "blue",
              },
              { label: destinationLabel, colorPalette: "blue" },
              { label: prototypeLabel, ...prototypeBadgeTokens },
              {
                label: languageLabel,
                colorPalette: watchedLanguage ? "teal" : "gray",
              },
              {
                label: `${mindstringCount} ${
                  mindstringCount === 1 ? "thought" : "thoughts"
                }`,
                colorPalette: mindstringCount > 0 ? "green" : "gray",
              },
            ]}
          />
        ) : null}

        <Field.Root invalid={!!errors.thoughtText}>
          <Text fontSize="xs" color="moveon.text.muted">
            {submitHelperText}
          </Text>

          <Textarea
            placeholder={`... enter your thoughts here... \n(press Enter to add new mindstrings)`}
            autoresize
            maxH="3xs"
            minH="3xs"
            {...register("thoughtText")}
            onKeyDown={handleKeyDown}
            aria-label="Thought text"
          />

          <Float placement="top-end">
            <Circle
              size="6"
              bg={countIndicatorBg}
              color={countIndicatorColor}
              fontSize="xs"
              fontWeight="medium"
            >
              {mindstringCount}
            </Circle>
          </Float>

          <Field.ErrorText>{errors.thoughtText?.message}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={!!errors.language}>
          <GenericSelector<SupportedLanguage>
            previewItems={languageItems}
            stateObject={selectedLanguages}
            stateObjectSetter={handleLanguageSelection}
            mode="select"
            allowMultiple={false}
            placeholder="Select language"
            width="full"
          />

          {detectedLanguage && !isDetectedLanguageSupported && (
            <Text color="moveon.warning" fontSize="xs" ml="1">
              <GenericIcon icon="inform" size="xs" /> Detected language{" "}
              {languageCodeToNameMap[detectedLanguage] ?? detectedLanguage} is
              not supported.
            </Text>
          )}

          {autoDetectedLanguage &&
            isDetectedLanguageSupported &&
            watchedLanguage === autoDetectedLanguage && (
              <Text color="moveon.text.muted" fontSize="xs" ml="1">
                Auto-detected:{" "}
                {languageCodeToNameMap[autoDetectedLanguage] ??
                  autoDetectedLanguage}
              </Text>
            )}

          {errors.language && (
            <Field.ErrorText>{errors.language.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Button
          type="submit"
          loading={isSubmitting}
          loadingText={PROFILE_SHAPING_LOADING_STATES.manualAdd.actionLabel}
          disabled={!isReadyToSubmit || isSubmitting}
          alignSelf="flex-end"
          bg="moveon.primary"
          minW={{ base: "10", sm: "auto" }}
          width="auto"
        >
          <GenericIcon icon="add" />
          <Text as="span" display={{ base: "none", sm: "inline" }}>
            {PROFILE_SHAPING_LABELS.manualPhase}
          </Text>
        </Button>

        <ThoughtDetailsReport mindstrings={mindstringsWithCounts} />
      </VStack>
    </form>
  );
}
